<template>
  <div v-if="showBanner" class="as-timezone-notice" role="status">
    <div class="as-timezone-notice__inner">
      <v-icon small class="as-timezone-notice__icon">mdi-clock-alert-outline</v-icon>
      <span class="as-timezone-notice__text">
        {{ $t('common.timezone.different-zone-notice', { offset: offsetLabel }) }}
      </span>
    </div>
  </div>
</template>

<script>
import dateUtil from '@/js/utils/dateUtil'

/**
 * Tells a viewer whose device clock differs from the studio's that class times on the page are
 * the studio's times, not theirs.
 *
 * Why it exists: every time in the app is rendered in studio time on purpose, so a participant
 * abroad sees times that look wrong for where they are standing and reads it as "the studio moved
 * my class". Stating both halves - you are elsewhere, these are studio times - answers that
 * directly. See docs/backlog.md [F14].
 *
 * Shown only when there is an actual difference, so the ~everyone who is in Latvia looking at a
 * Latvian studio never sees it. The comparison is by UTC offset, not zone name, so someone in
 * Helsinki (same clock, different zone id) is correctly treated as "same time".
 *
 * Not dismissable, on purpose (2026-09-22). It is one line, and it stays true for as long as the
 * viewer is abroad - it has to be readable next to the times it explains, on every page, for the
 * whole visit. The earlier version had a close button backed by sessionStorage; hiding the answer
 * to "why does this time look wrong?" is the confusion this notice exists to prevent.
 *
 * Studio time is currently the platform constant (Europe/Riga) for every studio. A per-studio
 * timezone was priced and deliberately deferred - see [F14] (b).
 */
export default {
  name: 'TimezoneNoticeBanner',
  computed: {
    offsetLabel () {
      return dateUtil.formatViewerOffsetFromStudio()
    },
    showBanner () {
      return dateUtil.isViewerInDifferentTimezone()
    }
  }
}
</script>

<style>
/* A full-width alert strip pinned to the top of the page, like a persistent phone notification.
   It stays on screen while the viewer scrolls the schedule, because that is exactly where the
   confusion happens: the note has to still be visible next to the times it explains. Solid amber
   (--color-pallet-4) with near-black text - white on amber fails contrast, and a tinted card
   inside the page gutter read as decoration rather than as a notice.

   Sticky offset: `--as-notice-sticky-top` is 0 by default, which is right wherever the app bar
   scrolls away with the page. A layout whose app bar is fixed sets the bar's height instead, so
   the strip parks below it rather than behind it. */
.as-timezone-notice {
  position: sticky;
  top: var(--as-notice-sticky-top, 0px);
  z-index: 6;
  width: 100%;
  background: var(--color-pallet-4);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

/* Full-bleed background, but the text lines up with the page content rather than the screen edge. */
.as-timezone-notice__inner {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 16px;
  min-height: 40px;
}

.as-timezone-notice__icon {
  flex-shrink: 0;
  color: #2B2000 !important;
}

.as-timezone-notice__text {
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.35;
  color: #2B2000;
}

@media (max-width: 599px) {
  .as-timezone-notice__inner {
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
  }

  .as-timezone-notice__icon {
    margin-top: 1px;
  }

  .as-timezone-notice__text {
    font-size: 0.8125rem;
  }
}
</style>
